import React, { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Collapse,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Folder as FolderIcon,
  FolderOpen,
  ExpandMore,
  ChevronRight,
  Add,
  CreateNewFolder,
  Check,
  Close,
  Storage,
  Delete,
} from '@mui/icons-material';
import { apiService } from '../services/api';

interface FolderNode {
  id: string;
  name: string;
  path: string;
  in_knowledge_base: boolean;
  file_count: number;
  children: FolderNode[];
}

interface FolderTreeProps {
  sourceType: string;
  selectedFolderId: string | null;
  onFolderSelect: (folderId: string | null, folderName: string, folderPath: string) => void;
  onRefresh?: () => void;
}

const FolderTree: React.FC<FolderTreeProps> = ({
  sourceType,
  selectedFolderId,
  onFolderSelect,
  onRefresh,
}) => {
  const [folders, setFolders] = useState<FolderNode[]>([]);
  const [rootFileCount, setRootFileCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [parentFolderForCreate, setParentFolderForCreate] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadFolderTree();
  }, [sourceType]);

  const loadFolderTree = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/api/v1/folders/tree/${sourceType}`);
      setFolders(response.data.folders || []);
      setRootFileCount(response.data.root_file_count || 0);
      
      // Auto-expand first level
      const firstLevelIds = (response.data.folders || []).map((f: FolderNode) => f.id);
      setExpanded(new Set(firstLevelIds));
    } catch (error) {
      console.error('Failed to load folder tree:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (folderId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpanded(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  const handleFolderClick = (folder: FolderNode | null) => {
    console.log('FolderTree handleFolderClick called with:', folder);
    if (folder === null) {
      onFolderSelect(null, 'Root', '/');
    } else {
      console.log('Calling onFolderSelect with:', folder.id, folder.name, folder.path);
      onFolderSelect(folder.id, folder.name, folder.path);
    }
  };

  const openCreateDialog = (parentId: string | null = null, event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    setParentFolderForCreate(parentId);
    setNewFolderName('');
    setCreateDialogOpen(true);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    
    try {
      setCreating(true);
      await apiService.post('/api/v1/folders/', {
        name: newFolderName.trim(),
        parent_id: parentFolderForCreate,
        source_type: sourceType,
        in_knowledge_base: true,
      });
      
      setCreateDialogOpen(false);
      setNewFolderName('');
      loadFolderTree();
      if (onRefresh) onRefresh();
    } catch (error: any) {
      console.error('Failed to create folder:', error);
      alert(error?.response?.data?.detail || 'Failed to create folder');
    } finally {
      setCreating(false);
    }
  };

  const toggleKnowledgeBase = async (folder: FolderNode, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await apiService.put(`/api/v1/folders/${folder.id}/kb?in_kb=${!folder.in_knowledge_base}`);
      loadFolderTree();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to toggle KB status:', error);
    }
  };

  const deleteFolder = async (folder: FolderNode, event: React.MouseEvent) => {
    event.stopPropagation();
    
    const confirmMsg = folder.file_count > 0 
      ? `Are you sure you want to delete "${folder.name}" and all ${folder.file_count} files in it? This cannot be undone.`
      : `Are you sure you want to delete "${folder.name}"?`;
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      // If folder has files, delete them first
      if (folder.file_count > 0) {
        await apiService.delete(`/api/v1/folders/${folder.id}/files`);
      }
      // Then delete the folder
      await apiService.delete(`/api/v1/folders/${folder.id}`);
      loadFolderTree();
      if (onRefresh) onRefresh();
    } catch (error: any) {
      console.error('Failed to delete folder:', error);
      alert(error?.response?.data?.detail || 'Failed to delete folder');
    }
  };

  const renderFolder = (folder: FolderNode, depth: number = 0) => {
    const isExpanded = expanded.has(folder.id);
    const isSelected = selectedFolderId === folder.id;
    const hasChildren = folder.children && folder.children.length > 0;

    return (
      <Box key={folder.id}>
        <ListItem
          disablePadding
          sx={{ 
            pl: depth * 2,
            display: 'flex',
            alignItems: 'center',
            '&:hover .folder-actions': { opacity: 1 },
          }}
        >
          <ListItemButton
            selected={isSelected}
            onClick={() => handleFolderClick(folder)}
            sx={{
              borderRadius: 1,
              py: 0.5,
              pr: 1,
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              '&.Mui-selected': {
                backgroundColor: 'action.selected',
                '&:hover': {
                  backgroundColor: 'action.selected',
                },
              },
            }}
          >
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); toggleExpand(folder.id, e); }}
                sx={{ mr: 0.5, p: 0.25 }}
              >
                {isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
              </IconButton>
            ) : (
              <Box sx={{ width: 24, mr: 0.5 }} />
            )}
            <ListItemIcon sx={{ minWidth: 28 }}>
              {isExpanded ? (
                <FolderOpen fontSize="small" sx={{ color: folder.in_knowledge_base ? 'primary.main' : 'grey.500' }} />
              ) : (
                <FolderIcon fontSize="small" sx={{ color: folder.in_knowledge_base ? 'primary.main' : 'grey.500' }} />
              )}
            </ListItemIcon>
            <Tooltip 
              title={
                <Box>
                  <Typography variant="body2" fontWeight={600}>{folder.name}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Path: {folder.path}
                  </Typography>
                  <br />
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {folder.file_count} file{folder.file_count !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              } 
              placement="right"
              enterDelay={400}
              arrow
            >
              <ListItemText 
                primary={folder.name}
                primaryTypographyProps={{ 
                  variant: 'body2', 
                  noWrap: true, 
                  sx: { 
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  } 
                }}
                sx={{ flex: 1, minWidth: 0, mr: 0.5 }}
              />
            </Tooltip>
            <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0, whiteSpace: 'nowrap' }}>
              ({folder.file_count})
            </Typography>
          </ListItemButton>
          
          {/* Action buttons - show on hover */}
          <Box
            className="folder-actions"
            sx={{
              display: 'flex',
              alignItems: 'center',
              opacity: 0,
              transition: 'opacity 0.2s',
              pr: 1,
            }}
          >
            <Tooltip title={folder.in_knowledge_base ? 'Remove from KB' : 'Add to KB'}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); toggleKnowledgeBase(folder, e); }}
                sx={{
                  p: 0.25,
                  color: folder.in_knowledge_base ? 'success.main' : 'grey.400',
                }}
              >
                <Storage sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add subfolder">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); openCreateDialog(folder.id, e); }}
                sx={{ p: 0.25, color: 'grey.500' }}
              >
                <Add sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); deleteFolder(folder, e); }}
                sx={{ p: 0.25, color: 'grey.400' }}
              >
                <Delete sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </ListItem>
        
        {hasChildren && (
          <Collapse in={isExpanded}>
            {folder.children.map(child => renderFolder(child, depth + 1))}
          </Collapse>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2" fontWeight={600}>
          📁 Folders
        </Typography>
        <Tooltip title="Create new folder">
          <IconButton size="small" onClick={() => openCreateDialog(null)}>
            <CreateNewFolder fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Folder List */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 1 }}>
        <List dense disablePadding>
          {/* Root/All Files option */}
          <ListItem disablePadding>
            <ListItemButton
              selected={selectedFolderId === 'root'}
              onClick={() => onFolderSelect('root', 'Root (Unsorted)', '/')}
              sx={{
                borderRadius: 1,
                mx: 1,
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <FolderIcon sx={{ color: 'warning.main' }} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">Root (Unsorted)</Typography>
                    {rootFileCount > 0 && (
                      <Chip
                        label={rootFileCount}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.7rem',
                          bgcolor: 'warning.light',
                        }}
                      />
                    )}
                  </Box>
                }
              />
            </ListItemButton>
          </ListItem>

          {/* Folder tree */}
          {folders.map(folder => renderFolder(folder))}

          {folders.length === 0 && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No folders yet
              </Typography>
              <Button
                size="small"
                startIcon={<CreateNewFolder />}
                onClick={() => openCreateDialog(null)}
                sx={{ mt: 1 }}
              >
                Create First Folder
              </Button>
            </Box>
          )}
        </List>
      </Box>

      {/* Create Folder Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create New Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Folder Name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
            sx={{ mt: 1 }}
            disabled={creating}
          />
          {parentFolderForCreate && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Creating inside selected folder
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateFolder}
            variant="contained"
            disabled={!newFolderName.trim() || creating}
            startIcon={creating ? <CircularProgress size={16} /> : <Check />}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FolderTree;

